import {
  useCampuses,
  useCreateGroup,
  useGroupTagFilter,
  useGroupTypes,
  useGroups,
  useJoinGroup,
  type Group,
} from '@church/data'
import { faChevronDown, faPlus, faUserPlus, faUsers } from '@church/ui-icons'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { GroupPicture } from '../components/GroupPicture.tsx'
import { RequireAccess } from '../components/RequireAccess.tsx'
import {
  Button,
  Card,
  Chip,
  EmptyState,
  ErrorNote,
  Field,
  Icon,
  IconButton,
  Input,
  listRow,
  PageHeader,
  Select,
  SidePanel,
  ErrorState,
  ListSkeleton,
  Textarea,
} from '../components/ui.tsx'
import { DisplayOptions } from '../components/DisplayOptions.tsx'
import { PageAction } from '../components/PageAction.tsx'
import { useListDisplay } from '../lib/display.ts'

/**
 * The group directory.
 *
 * What a person sees here is already decided by the server — private types are
 * simply absent — so this screen filters and renders, and takes its buttons
 * from each group's own `access` flags rather than from the viewer's role.
 */
export const Route = createFileRoute('/_authed/_shell/groups/')({
  component: GroupsScreen,
})

/** What a group's row shows on a phone; everything shows from `sm` up. */
const ROW_FIELDS = [
  { id: 'when', label: 'Type and schedule', default: true },
  { id: 'where', label: 'Where it meets', default: false },
  { id: 'members', label: 'Members', default: true },
  { id: 'tags', label: 'Tags', default: false },
  { id: 'picture', label: 'Picture', default: true },
] as const
type RowField = (typeof ROW_FIELDS)[number]['id']

function GroupsScreen() {
  const [typeId, setTypeId] = useState('')
  const [mine, setMine] = useState(false)
  const [openSpots, setOpenSpots] = useState(false)
  const [creating, setCreating] = useState(false)
  const display = useListDisplay('groups', ROW_FIELDS)
  const tagFilter = useGroupTagFilter()

  const { data: types } = useGroupTypes()
  const {
    groups,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    error,
    refetch,
    isRefetching,
  } = useGroups({
    ...(typeId ? { typeId } : {}),
    ...(mine ? { mine: true } : {}),
    ...(openSpots ? { hasOpenSpots: true } : {}),
    ...(tagFilter.param ? { tags: tagFilter.param } : {}),
  })

  return (
    <div className="space-y-4">
      <PageHeader title="Groups" />

      <NewGroupPanel open={creating} onClose={() => setCreating(false)} />

      <div className="flex flex-wrap items-center gap-2">
        <Select
          aria-label="Group type"
          className="min-w-36 flex-1 sm:max-w-52"
          value={typeId}
          onChange={(e) => setTypeId(e.target.value)}
        >
          <option value="">All types</option>
          {(types ?? []).map((type) => (
            <option key={type.id} value={type.id}>
              {type.name}
            </option>
          ))}
        </Select>
        {tagFilter.facets.map((facet) => (
          <Select
            key={facet.id}
            aria-label={facet.name}
            className="min-w-32 flex-1 sm:max-w-44"
            value={tagFilter.value(facet.id)}
            onChange={(e) => tagFilter.set(facet.id, e.target.value)}
          >
            <option value="">Any {facet.name.toLowerCase()}</option>
            {facet.tags.map((tag) => (
              <option key={tag.id} value={tag.id}>
                {tag.name}
              </option>
            ))}
          </Select>
        ))}
        <FilterToggle on={mine} onChange={setMine}>
          Mine
        </FilterToggle>
        <FilterToggle on={openSpots} onChange={setOpenSpots}>
          Open spots
        </FilterToggle>
        <div className="ml-auto flex items-center gap-1">
          <DisplayOptions fields={ROW_FIELDS} shown={display.shown} onChange={display.set} />
          <RequireAccess module="groups" level="editor">
            <PageAction icon={faPlus} label="New group" onClick={() => setCreating(true)} />
          </RequireAccess>
        </div>
      </div>

      {error && groups.length === 0 ? (
        <ErrorState
          error={error}
          safe="Nothing was changed: your groups are safe on the server."
          retrying={isRefetching}
          onRetry={() => void refetch()}
        />
      ) : isLoading ? (
        <Card bleed>
          <ListSkeleton rows={6} />
        </Card>
      ) : groups.length === 0 ? (
        <EmptyState
          title={mine ? 'You are not in any groups yet' : 'No groups here'}
          hint={
            mine
              ? 'Groups you join or lead show up here.'
              : 'Groups are small groups, teams and classes — anything with a roster.'
          }
          action={
            mine ? (
              <Button variant="secondary" onClick={() => setMine(false)}>
                See every group
              </Button>
            ) : (
              <RequireAccess module="groups" level="editor">
                <Button icon={faPlus} onClick={() => setCreating(true)}>
                  New group
                </Button>
              </RequireAccess>
            )
          }
        />
      ) : (
        <Card bleed>
          <ul className="divide-y divide-border-subtle">
            {groups.map((group) => (
              <GroupRow key={group.id} group={group} hide={display.hide} />
            ))}
          </ul>
        </Card>
      )}

      {hasNextPage && (
        <Button
          variant="ghost"
          icon={faChevronDown}
          pending={isFetchingNextPage}
          pendingLabel="Loading more groups…"
          onClick={() => void fetchNextPage()}
        >
          More
        </Button>
      )}
    </div>
  )
}

/** A filter that is on or off, as a pill rather than a checkbox. */
function FilterToggle({
  on,
  onChange,
  children,
}: {
  on: boolean
  onChange: (on: boolean) => void
  children: string
}) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={() => onChange(!on)}
      className={`rounded-full px-3 py-2 text-sm font-medium transition-colors duration-(--duration-fast) ${
        on
          ? 'bg-brand-solid text-brand-on-solid'
          : 'border border-border-default text-content-muted hover:bg-surface-sunken'
      }`}
    >
      {children}
    </button>
  )
}

/** A group in the directory: name first, the rest as the display options choose. */
function GroupRow({ group, hide }: { group: Group; hide: (field: RowField) => string }) {
  const join = useJoinGroup(group.id)
  const full = group.capacity !== null && group.memberCount >= group.capacity

  return (
    <li className={`${listRow} flex items-center gap-3`}>
      <span className={hide('picture')}>
        <GroupPicture group={group} className="size-10 rounded-lg" />
      </span>
      <Link
        to="/groups/$id"
        params={{ id: group.id }}
        className="group min-w-0 flex-1 no-underline"
      >
        <span className="flex items-center gap-2">
          <span className="truncate text-base font-medium text-content-default group-hover:text-content-brand">
            {group.name}
          </span>
          <Relationship group={group} />
        </span>
        <span className={`block truncate text-sm text-content-muted ${hide('when')}`}>
          {[group.groupTypeName, group.scheduleSummary].filter(Boolean).join(' · ')}
        </span>
        {group.location && (
          <span className={`block truncate text-xs text-content-subtle ${hide('where')}`}>
            {group.location}
          </span>
        )}
        {group.tags.length > 0 && (
          <span className={`mt-1 flex flex-wrap gap-1 ${hide('tags')}`}>
            {group.tags.map((tag) => (
              <Chip key={tag.id}>{tag.name}</Chip>
            ))}
          </span>
        )}
      </Link>
      <span className={hide('members')}>
        <Chip tone={full ? 'warning' : 'neutral'}>
          <Icon icon={faUsers} className="size-3" />
          {group.memberCount}
          {group.capacity !== null ? `/${group.capacity}` : ''}
          <span className="sr-only">{full ? ' members, full' : ' members'}</span>
        </Chip>
      </span>
      {(group.access.canJoin || group.access.canRequestToJoin) && (
        <IconButton
          icon={faUserPlus}
          label={group.access.canJoin ? `Join ${group.name}` : `Ask to join ${group.name}`}
          variant="secondary"
          disabled={join.isPending}
          onClick={() => join.mutate({})}
        />
      )}
      {join.error ? <ErrorNote error={join.error} /> : null}
    </li>
  )
}

function Relationship({ group }: { group: Group }) {
  if (group.archivedAt) return <Badge tone="muted">Archived</Badge>
  if (group.relationship === 'leader') return <Badge tone="brand">You lead</Badge>
  if (group.relationship === 'member') return <Badge tone="brand">Member</Badge>
  if (group.relationship === 'requested') return <Badge tone="muted">Requested</Badge>
  return null
}

function Badge({ children, tone }: { children: string; tone: 'brand' | 'muted' }) {
  const styles =
    tone === 'brand' ? 'bg-brand-soft text-content-brand' : 'bg-surface-sunken text-content-muted'
  return <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs ${styles}`}>{children}</span>
}

const EMPTY_GROUP = {
  groupTypeId: '',
  campusId: '',
  name: '',
  description: '',
  location: '',
  capacity: '',
}

function NewGroupPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const create = useCreateGroup()
  const { data: types } = useGroupTypes()
  const { data: campuses } = useCampuses()
  const [form, setForm] = useState(EMPTY_GROUP)
  // A fresh form each time it opens (it stays mounted, so it can slide out).
  const [openedFor, setOpenedFor] = useState(open)
  if (open !== openedFor) {
    setOpenedFor(open)
    if (open) setForm(EMPTY_GROUP)
  }
  const close = () => {
    create.reset()
    onClose()
  }

  const set = (key: keyof typeof form) => (value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }))

  return (
    <SidePanel
      open={open}
      onOpenChange={(next) => {
        if (!next) close()
      }}
      title="New group"
      description="The meeting schedule is set on the group’s own page, where it can be previewed."
      primary={
        <Button
          icon={faPlus}
          className="py-1.5"
          pending={create.isPending}
          disabled={form.name.trim() === '' || form.groupTypeId === ''}
          onClick={() =>
            create.mutate(
              {
                groupTypeId: form.groupTypeId,
                campusId: form.campusId === '' ? null : form.campusId,
                name: form.name.trim(),
                description: form.description.trim() === '' ? null : form.description.trim(),
                location: form.location.trim() === '' ? null : form.location.trim(),
                capacity: form.capacity === '' ? null : Number(form.capacity),
              },
              { onSuccess: close },
            )
          }
        >
          Create
        </Button>
      }
    >
      <div className="space-y-3">
        <Field label="Name">
          <Input
            placeholder="Tuesday Night Small Group"
            value={form.name}
            onChange={(e) => set('name')(e.target.value)}
          />
        </Field>
        <Field label="Type">
          <Select value={form.groupTypeId} onChange={(e) => set('groupTypeId')(e.target.value)}>
            <option value="">Choose a type…</option>
            {(types ?? []).map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Campus" description="Optional — leave blank for a church-wide group.">
          <Select value={form.campusId} onChange={(e) => set('campusId')(e.target.value)}>
            <option value="">No campus</option>
            {(campuses ?? []).map((campus) => (
              <option key={campus.id} value={campus.id}>
                {campus.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Meets at" description="“The Okafors’ house”, “Room 201”.">
          <Input value={form.location} onChange={(e) => set('location')(e.target.value)} />
        </Field>
        <Field label="Capacity" description="Blank means no limit.">
          <Input
            type="number"
            min={1}
            value={form.capacity}
            onChange={(e) => set('capacity')(e.target.value)}
          />
        </Field>
      </div>

      <Field label="Description">
        <Textarea
          rows={2}
          value={form.description}
          onChange={(e) => set('description')(e.target.value)}
        />
      </Field>

      <ErrorNote error={create.error} />
    </SidePanel>
  )
}
